import { FILE_CONSTANTS } from "./documentUpload";

// API Constants
export const API_CONSTANTS = {
  // Base URLs
  DEFAULT_BASE_URL: "http://localhost:9999",
  FALLBACK_BASE_URL: "",

  // Endpoints
  ENDPOINTS: {
    DOCUMENTS_UPLOAD: "/documents/upload",
    HEALTH: "/health",
    VERSION: "/version",
    AUTH_TOKEN: "/auth/token",
  },

  // HTTP Status Codes
  STATUS_CODES: {
    OK: {
      CODE: 200,
      MESSAGE: "OK",
    },
    CREATED: {
      CODE: 201,
      MESSAGE: "Created",
    },
    ACCEPTED: {
      CODE: 202,
      MESSAGE: "Accepeted",
    },
    NO_CONTENT: {
      CODE: 204,
      MESSAGE: "No Content",
    },
    BAD_REQUEST: {
      CODE: 400,
      MESSAGE: "Bad Request",
    },
    UNAUTHORIZED: {
      CODE: 401,
      MESSAGE: "Unauthorized",
    },
    FORBIDDEN: {
      CODE: 403,
      MESSAGE: "Forbidden",
    },
    NOT_FOUND: {
      CODE: 404,
      MESSAGE: "Not Found",
    },
    CONFLICT: {
      CODE: 409,
      MESSAGE: "Conflict",
    },
    PAYLOAD_TOO_LARGE: {
      CODE: 413,
      MESSAGE: "Payload Too Large",
    },
    UNSUPPORTED_MEDIA_TYPE: {
      CODE: 415,
      MESSAGE: "Unsupported Media Type",
    },
    UNPROCESSABLE_ENTITY: {
      CODE: 422,
      MESSAGE: "Unprocessable Entity",
    },
    INTERNAL_SERVER_ERROR: {
      CODE: 500,
      MESSAGE: "Internal Server Error",
    },
    INSUFFICIENT_STORAGE: {
      CODE: 507,
      MESSAGE: "Insufficent Storage",
    },
  },

  TIMEOUTS: {
    REQUEST_TIMEOUT: 10000, // 10 seconds
  },

  // Headers
  HEADERS: {
    CONTENT_TYPE: "Content-Type",
    AUTHORIZATION: "Authorization",
    API_KEY: "X-API-Key",
    // Partner context headers
    PARTNER_ID: "X-Partner-ID",
    CORRELATION_ID: "X-Correlation-ID",
    // Documents upload headers
    FILE_NAME: "X-File-Name",
    RECORD_TYPE: "X-Record-Type",
    VENDOR_ID_REF: "X-Vendor-Id-Ref",
    CLIENT_ID_REF: "X-Client-Id-Ref",
    MIME_TYPE: "X-Mime-Type",
    STORAGE_ONLY: "X-Storage-Only",
    // Multi-sheet workbook upload (ADO #4267 / MSW-1).
    // Set on a per-(file, sheet) upload to name the workbook sheet to
    // ingest. Absent preserves the legacy single-sheet behavior.
    SHEET_NAME: "X-Sheet-Name",
    // Multi-client ("book of business") reimbursement upload (#5309 / ADR-057).
    // "true" routes the document onto the multi-client load + review path; each
    // row resolves its own client/policy. Absent preserves the single-client path.
    MULTI_CLIENT: "X-Multi-Client",
  },

  // Content Types
  CONTENT_TYPES: {
    MULTIPART_FORM_DATA: "multipart/form-data",
    APPLICATION_JSON: "application/json",
    APPLICATION_OCTET_STREAM: "application/octet-stream",
  },
} as const;

// Authentication Constants
export const AUTH_CONSTANTS = {
  // Storage keys
  STORAGE_KEYS: {
    AUTH_TOKEN: "authToken",
    USER_ID: "user",
    AUTH_ME_DATA: "auth_me_data",
    RA_PERMISSIONS: "ra_permissions",
    PARTNER_BRANDING: "partner_branding",
    API_VERSION: "api_version",
    PARTNER_CONFIG: "partner_config",
    ACTIVE_PARTNER_ID: "active_partner_id",
    LAST_PARTNER_ID: "last_partner_id",
    RECENT_PARTNERS: "recent_partners",
    AUTH_REDIRECT_URL: "auth_redirect_url",
  },

  ERRORS: {
    ACCESS_DENIED: "access_denied",
  },

  // Token types
  TOKEN_TYPES: {
    BEARER: "bearer" as const,
    API_KEY: "api-key" as const,
    NONE: "none" as const,
  },

  // Default values
  DEFAULT_TOKEN: "dev-token",
  DEFAULT_USER_ID: "default-user",
} as const;

// UI Constants
export const UI_CONSTANTS = {
  // Animation durations (in milliseconds)
  ANIMATION: {
    TRANSITION_DURATION: 200,
    PROGRESS_UPDATE_DELAY: 300,
    TOAST_DURATION: 4000,
  },

  // Progress thresholds
  PROGRESS: {
    MIN: 0,
    MAX: 100,
    STEP: 1,
  },

  // File size formatting
  FILE_SIZE: {
    UNITS: ["Bytes", "KB", "MB", "GB"] as const,
    BASE: 1024,
    DECIMAL_PLACES: 2,
  },

  // Toast configuration
  TOAST: {
    POSITION: "top-right" as const,
    STYLE: {
      background: "#363636",
      color: "#fff",
    },
  },
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  UPLOAD: {
    NO_FILES: "No files to upload",
    UPLOAD_FAILED: "Upload failed. Please try again.",
    FILE_TOO_LARGE: `File size must be less than ${FILE_CONSTANTS.MAX_FILE_SIZE_MB}MB`,
    FORBIDDEN:
      "Upload was blocked by the gateway — often due to the file's size or content type. Please try a smaller file or a different format, or contact support if the problem persists.",
    UNSUPPORTED_TYPE: "Only PDF files are supported",
    NETWORK_ERROR: "Network error. Please check your connection.",
    SERVER_ERROR: "Server error. Please try again later.",
    AUTHENTICATION_ERROR: "Authentication failed. Please check your credentials.",
  },
  VALIDATION: {
    FILE_REQUIRED: "Please select a file to upload",
    INVALID_FILE_TYPE: "Invalid file type. Only PDF files are allowed.",
    FILE_SIZE_EXCEEDED: "File size exceeds the maximum limit.",
  },
  GENERAL: {
    UNKNOWN_ERROR: "An unexpected error occurred.",
    RETRY_SUGGESTION: "Please try again or contact support if the problem persists.",
  },
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  UPLOAD: {
    FILES_UPLOADED: "All files uploaded successfully!",
    FILE_UPLOADED: "File uploaded successfully!",
  },
  AUTH: {
    TOKEN_SET: "Authentication token set successfully",
    USER_ID_SET: "User ID set successfully",
  },
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "authToken",
  USER_ID: "userId",
  UPLOAD_HISTORY: "uploadHistory",
  USER_PREFERENCES: "userPreferences",
} as const;

// Development Constants
export const DEV_CONSTANTS = {
  // Development flags
  ENABLE_DEBUG_LOGGING: import.meta.env.DEV,
  ENABLE_MOCK_UPLOADS: false,

  // Development URLs
  MOCK_API_URL: "http://localhost:3001",

  // Development timeouts
  TIMEOUTS: {
    UPLOAD_TIMEOUT: 30000, // 30 seconds
    REQUEST_TIMEOUT: 10000, // 10 seconds
  },
} as const;

// File Processing Constants
export const FILE_PROCESSING = {
  // Chunk sizes for large file uploads
  CHUNK_SIZE: 1024 * 1024, // 1MB chunks

  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY_BETWEEN_ATTEMPTS: 1000, // 1 second
    BACKOFF_MULTIPLIER: 2,
  },

  // Progress update throttling
  PROGRESS_THROTTLE: 100, // Update progress every 100ms
} as const;
